import React, { useEffect, useMemo, useState } from 'react';
import {
    useAutomations,
    useAutomationLogs,
    useAutomationTemplates,
    useAutomationSettings,
    useAutomationPlaybooks,
} from '../hooks/useAutomations';
import AutomationList from '../components/automations/AutomationList';
import AutomationEditor from '../components/automations/AutomationEditor';
import AutomationLogs from '../components/automations/AutomationLogs';
import AutomationTemplates from '../components/automations/AutomationTemplates';
import AutomationOutbox from '../components/automations/AutomationOutbox';
import AutomationSettings from '../components/automations/AutomationSettings';
import AutomationPlaybooks from '../components/automations/AutomationPlaybooks';
import { useToast } from '../components/Toast';
import '../crm.css';

const createEmptyAutomation = () => ({
    id: '',
    name: '',
    enabled: true,
    trigger: 'lead.created',
    conditions: [],
    actions: [],
    rateLimit: { perEntityMins: '' },
    delayMins: '',
});

const AutomationsPage = () => {
    const { automations, loading, error, refetch, create, update, toggle } = useAutomations();
    const { templates, create: createTemplate } = useAutomationTemplates();
    const { settings, save: saveSettings } = useAutomationSettings();
    const { playbooks, save: savePlaybook } = useAutomationPlaybooks();
    const [selectedId, setSelectedId] = useState('');
    const [draft, setDraft] = useState(createEmptyAutomation());
    const [isNew, setIsNew] = useState(false);
    const [authError, setAuthError] = useState(false);
    const { showSuccess, showError } = useToast();
    const [logsRange, setLogsRange] = useState('7d');
    const { logs, loading: logsLoading, error: logsError, refetch: refetchLogs } = useAutomationLogs(logsRange);
    const [activeTab, setActiveTab] = useState('playbooks');
    const [showFlow, setShowFlow] = useState(false);
    const [showSetup, setShowSetup] = useState(false);
    const [setupStep, setSetupStep] = useState(1);
    const [setupChannels, setSetupChannels] = useState([]);
    const [setupPlaybooks, setSetupPlaybooks] = useState({});
    const [creatingDefaults, setCreatingDefaults] = useState(false);

    const rulesAutomations = useMemo(
        () => automations.filter((item) => !item.id?.startsWith('pb_')),
        [automations],
    );

    const CHANNELS = [
        { key: 'whatsapp', label: 'WhatsApp', group: 'Messaging' },
        { key: 'sms', label: 'SMS', group: 'Messaging' },
        { key: 'email', label: 'Email', group: 'Messaging' },
        { key: 'telegram', label: 'Telegram', group: 'Messaging' },
        { key: 'instagram_dm', label: 'Instagram DM', group: 'Meta Social' },
        { key: 'facebook_messenger', label: 'Facebook Messenger', group: 'Meta Social' },
        { key: 'instagram_comment_reply', label: 'IG Comment Reply', group: 'Social Replies' },
        { key: 'facebook_comment_reply', label: 'FB Comment Reply', group: 'Social Replies' },
        { key: 'linkedin_message', label: 'LinkedIn (manual)', group: 'Other' },
        { key: 'webhook', label: 'Webhook', group: 'Other' },
    ];

    const helpCopy = {
        playbooks: 'Turn on workflows. Pick templates + delays.',
        templates: 'Reusable messages. Use variables like {lead.name}.',
        outbox: 'Everything queued/sent/failed. Approvals happen here.',
        rules: 'Advanced logic. Most users should use Playbooks.',
        settings: 'Safety limits: quiet hours, approvals, opt-out.',
    };

    const openSetup = () => {
        setShowSetup(true);
        setSetupStep(1);
        setSetupChannels(settings?.enabledChannels || CHANNELS.map((channel) => channel.key));
        const nextPlaybooks = {};
        playbooks.forEach((pb) => {
            nextPlaybooks[pb.id] = pb.enabled;
        });
        setSetupPlaybooks(nextPlaybooks);
    };

    const createDefaultTemplates = async () => {
        setCreatingDefaults(true);
        try {
            const existingNames = new Set(templates.map((tpl) => tpl.name));
            const defaults = [
                {
                    name: 'New Lead — Instant Reply',
                    channel: 'whatsapp',
                    category: 'new_lead',
                    body: 'Hi {lead.name}, thanks for your enquiry. This is {agent.name} from {company.name}.',
                    language: 'en',
                },
                {
                    name: 'Site Visit — Confirmation',
                    channel: 'whatsapp',
                    category: 'site_visit',
                    body: 'Hi {lead.name}, your site visit is confirmed for {followup.date} at {followup.time}.',
                    language: 'en',
                },
                {
                    name: 'Booking — Payment Instructions',
                    channel: 'email',
                    category: 'booking',
                    body: 'Hi {lead.name}, please share payment details to proceed with booking.',
                    language: 'en',
                },
            ];
            for (const tpl of defaults) {
                if (!existingNames.has(tpl.name)) {
                    await createTemplate(tpl);
                }
            }
            showSuccess('Default templates created');
        } catch (err) {
            showError(err.message || 'Failed to create defaults');
        } finally {
            setCreatingDefaults(false);
        }
    };

    const handleSetupFinish = async () => {
        try {
            await saveSettings({
                ...(settings || {}),
                enabledChannels: setupChannels,
            });
            const updates = Object.entries(setupPlaybooks);
            for (const [id, enabled] of updates) {
                await savePlaybook(id, { enabled });
            }
            showSuccess('Guided setup saved');
            setShowSetup(false);
        } catch (err) {
            showError(err.message || 'Failed to save setup');
        }
    };

    useEffect(() => {
        if (error) {
            const message = error.message || '';
            if (message.toLowerCase().includes('admin') || message.includes('403')) {
                setAuthError(true);
            }
        }
    }, [error]);

    useEffect(() => {
        if (!selectedId && automations.length > 0) {
            setSelectedId(automations[0].id);
        }
    }, [automations, selectedId]);

    useEffect(() => {
        if (activeTab !== 'rules') return;
        if (!selectedId && rulesAutomations.length > 0) {
            setSelectedId(rulesAutomations[0].id);
        }
        if (selectedId && rulesAutomations.length > 0 && !rulesAutomations.find((item) => item.id === selectedId)) {
            setSelectedId(rulesAutomations[0].id);
        }
    }, [activeTab, rulesAutomations, selectedId]);

    const selected = useMemo(() => (
        automations.find((item) => item.id === selectedId) || null
    ), [automations, selectedId]);

    useEffect(() => {
        if (selected && !isNew) {
            setDraft({
                ...selected,
                rateLimit: { perEntityMins: selected.rateLimit?.perEntityMins || '' },
                delayMins: selected.delayMins || '',
            });
        }
    }, [selected, isNew]);

    const handleNew = () => {
        setIsNew(true);
        setSelectedId('');
        setDraft(createEmptyAutomation());
    };

    const handleSelect = (automation) => {
        setIsNew(false);
        setSelectedId(automation.id);
    };

    const handleSave = async () => {
        const payload = {
            ...draft,
            rateLimit: draft.rateLimit?.perEntityMins
                ? { perEntityMins: Number(draft.rateLimit.perEntityMins) }
                : undefined,
            delayMins: draft.delayMins ? Number(draft.delayMins) : undefined,
        };
        try {
            if (isNew) {
                const created = await create(payload);
                showSuccess('Automation created');
                setIsNew(false);
                await refetch();
                setSelectedId(created.id);
            } else {
                await update(draft.id, payload);
                showSuccess('Automation updated');
                await refetch();
            }
        } catch (err) {
            showError(err.message || 'Failed to save automation');
        }
    };

    const handleToggle = async (automation) => {
        try {
            await toggle(automation.id, !automation.enabled);
            await refetch();
        } catch (err) {
            showError(err.message || 'Failed to toggle automation');
        }
    };

    const handleDuplicate = async () => {
        try {
            const payload = {
                ...draft,
                id: undefined,
                name: `${draft.name || 'Automation'} (Copy)`,
            };
            const created = await create(payload);
            showSuccess('Automation duplicated');
            await refetch();
            setSelectedId(created.id);
        } catch (err) {
            showError(err.message || 'Failed to duplicate automation');
        }
    };

    if (authError) {
        return (
            <div className="crm-container">
                <div className="crm-empty-state">
                    <h3>Not authorized</h3>
                    <p>Only admins can manage automations.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="crm-container">
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Automation Center</h1>
                    <p className="crm-page-subtitle">Playbooks, messages, and advanced rules in one place.</p>
                </div>
                <button className="crm-btn crm-btn-secondary" onClick={openSetup}>
                    Guided Setup
                </button>
            </div>

            <div className="crm-tab-bar" style={{ marginBottom: '16px' }}>
                {['playbooks', 'templates', 'outbox', 'rules', 'settings'].map((tab) => (
                    <button
                        key={tab}
                        className={`crm-tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                        type="button"
                    >
                        {tab === 'templates' ? 'Templates' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            <div className="crm-card crm-card-subtle" style={{ marginBottom: '16px' }}>
                <div className="crm-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>What is this?</div>
                        <div className="crm-muted">{helpCopy[activeTab]}</div>
                    </div>
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => setShowFlow(true)}>
                        Learn the flow
                    </button>
                </div>
            </div>

            {activeTab === 'playbooks' && <AutomationPlaybooks />}
            {activeTab === 'templates' && <AutomationTemplates />}
            {activeTab === 'outbox' && <AutomationOutbox />}

            {activeTab === 'rules' && (
                <>
                    <div className="crm-page-header" style={{ marginTop: '8px' }}>
                        <div>
                            <h2 className="crm-card-title">Rules Builder</h2>
                            <p className="crm-muted">Advanced triggers, conditions, and actions.</p>
                        </div>
                        <button className="crm-btn crm-btn-primary" onClick={handleNew}>
                            + New Rule
                        </button>
                    </div>
                    <div className="crm-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
                        <AutomationList
                            loading={loading}
                            automations={rulesAutomations}
                            selectedId={selectedId}
                            onSelect={handleSelect}
                            onToggle={handleToggle}
                            emptyStateText="Start from Playbooks unless you need custom logic."
                        />
                        <AutomationEditor
                            draft={draft}
                            setDraft={setDraft}
                            isNew={isNew}
                            templates={templates}
                            onSave={handleSave}
                            onCancel={() => {
                                setIsNew(false);
                                if (selected) {
                                    setDraft({
                                        ...selected,
                                        rateLimit: { perEntityMins: selected.rateLimit?.perEntityMins || '' },
                                        delayMins: selected.delayMins || '',
                                    });
                                } else {
                                    setDraft(createEmptyAutomation());
                                }
                            }}
                            onDuplicate={handleDuplicate}
                            disabled={loading || !draft}
                        />
                    </div>

                    <div style={{ marginTop: '24px' }}>
                        <AutomationLogs
                            logs={logs}
                            loading={logsLoading}
                            error={logsError}
                            range={logsRange}
                            onRangeChange={setLogsRange}
                            onRefresh={refetchLogs}
                        />
                    </div>
                </>
            )}

            {activeTab === 'settings' && <AutomationSettings />}

            {showFlow && (
                <div className="crm-modal-overlay" onClick={() => setShowFlow(false)}>
                    <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <h3 className="crm-modal-title">Automation Flow</h3>
                            <button className="crm-modal-close" onClick={() => setShowFlow(false)}>✕</button>
                        </div>
                        <div className="crm-modal-body">
                            <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '8px' }}>
                                <li>Create templates with variables.</li>
                                <li>Enable playbooks (recommended) or rules.</li>
                                <li>Events create Outbox items.</li>
                                <li>Approve or send manually if needed.</li>
                                <li>Outbox tracks sent/failed/canceled.</li>
                            </ol>
                        </div>
                        <div className="crm-modal-footer">
                            <button className="crm-btn crm-btn-primary" onClick={() => setShowFlow(false)}>
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSetup && (
                <div className="crm-modal-overlay" onClick={() => setShowSetup(false)}>
                    <div className="crm-modal crm-modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <h3 className="crm-modal-title">Guided Setup</h3>
                            <button className="crm-modal-close" onClick={() => setShowSetup(false)}>✕</button>
                        </div>
                        <div className="crm-modal-body">
                            {setupStep === 1 && (
                                <div>
                                    <h4 style={{ marginTop: 0 }}>Step 1: Choose channels</h4>
                                    <div style={{ display: 'grid', gap: '12px' }}>
                                        {Array.from(new Set(CHANNELS.map((channel) => channel.group))).map((group) => (
                                            <div key={group}>
                                                <div className="crm-muted" style={{ marginBottom: '6px' }}>{group}</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                    {CHANNELS.filter((channel) => channel.group === group).map((channel) => (
                                                        <label key={channel.key} className="crm-inline-check">
                                                            <input
                                                                type="checkbox"
                                                                checked={setupChannels.includes(channel.key)}
                                                                onChange={(e) => {
                                                                    const next = e.target.checked
                                                                        ? Array.from(new Set([...setupChannels, channel.key]))
                                                                        : setupChannels.filter((item) => item !== channel.key);
                                                                    setSetupChannels(next);
                                                                }}
                                                            />
                                                            <span>{channel.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {setupStep === 2 && (
                                <div>
                                    <h4 style={{ marginTop: 0 }}>Step 2: Create/import templates</h4>
                                    <p className="crm-muted">We can create a few defaults to get you started.</p>
                                    <button className="crm-btn crm-btn-primary" onClick={createDefaultTemplates} disabled={creatingDefaults}>
                                        {creatingDefaults ? 'Creating…' : 'Create default templates'}
                                    </button>
                                </div>
                            )}

                            {setupStep === 3 && (
                                <div>
                                    <h4 style={{ marginTop: 0 }}>Step 3: Enable recommended playbooks</h4>
                                    <div style={{ display: 'grid', gap: '10px' }}>
                                        {playbooks.map((pb) => (
                                            <div key={pb.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{pb.name}</div>
                                                    <div className="crm-muted">{pb.description}</div>
                                                </div>
                                                <label className="crm-inline-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(setupPlaybooks[pb.id])}
                                                        onChange={(e) => setSetupPlaybooks({
                                                            ...setupPlaybooks,
                                                            [pb.id]: e.target.checked,
                                                        })}
                                                    />
                                                    <span>Enabled</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="crm-modal-footer">
                            <button
                                className="crm-btn crm-btn-secondary"
                                onClick={() => setSetupStep(Math.max(1, setupStep - 1))}
                                disabled={setupStep === 1}
                            >
                                Back
                            </button>
                            {setupStep < 3 ? (
                                <button className="crm-btn crm-btn-primary" onClick={() => setSetupStep(setupStep + 1)}>
                                    Next
                                </button>
                            ) : (
                                <button className="crm-btn crm-btn-primary" onClick={handleSetupFinish}>
                                    Finish setup
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutomationsPage;
